import type * as FavCache from '@finn-no/favorite-api-browser-client';
import { i18n } from '@lingui/core';
import { modal as modalClasses } from '@warp-ds/css/component-classes';
import { Alert, Button } from '@warp-ds/react';
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    useState,
    type JSX,
} from 'react';
import MoreOptionsFolder from './FolderButton';
import Pagination from './Pagination';
import Search from './Search';
import usePagination from './hooks/usePagination';
import useSearch from './hooks/useSearch';

type FoldersTemplateProps = {
    itemType: FavCache.ItemType;
    itemId: number;
    api: FavCache.FavoriteBrowserCache;
    closeCallback: () => void;
    folders: FavCache.FavoriteListType[];
    foldersWithItem: FavCache.ItemFolder;
    setErrorMessage: (error: string) => void;
    toggleCreateNewFolder?: () => void;
};

const FoldersTemplate = forwardRef(
    (
        {
            itemType,
            itemId,
            api,
            closeCallback,
            folders,
            foldersWithItem,
            setErrorMessage,
            toggleCreateNewFolder,
        }: FoldersTemplateProps,
        forwardRef: React.Ref<HTMLButtonElement>,
    ) => {
        return (
            <div className="grid md:grid-cols-2 gap-16 mt-24 pb-24">
                {folders.map((fl, index) => (
                    <div key={fl.folderid}>
                        <MoreOptionsFolder
                            ref={
                                !toggleCreateNewFolder && index === 0
                                    ? forwardRef
                                    : undefined
                            }
                            adExists={
                                !!foldersWithItem.folderIds &&
                                foldersWithItem.folderIds.includes(fl.folderid)
                            }
                            itemType={itemType}
                            itemId={itemId}
                            api={api}
                            closeCallback={closeCallback}
                            folder={fl}
                            handleError={(message) => setErrorMessage(message)}
                            key={fl.folderid}
                        />
                    </div>
                ))}
            </div>
        );
    },
);

type ContentProps = {
    itemType: FavCache.ItemType;
    itemId: number;
    api: FavCache.FavoriteBrowserCache;
    closeCallback: () => void;
    errorMessage: string;
    foldersWithItem: FavCache.ItemFolder;
    items: FavCache.FavoriteListType[];
    setErrorMessage: (error: string) => void;
    toggleCreateNewFolder: () => void;
    initialFocusRef: React.RefObject<HTMLElement | null>;
    mobileFocusHeading: boolean;
};

export const ModalContent: (
    props: ContentProps,
) => [JSX.Element, JSX.Element, JSX.Element] = (props) => {
    const {
        itemType,
        itemId,
        api,
        closeCallback,
        errorMessage,
        foldersWithItem,
        items,
        setErrorMessage,
        toggleCreateNewFolder,
        initialFocusRef,
        mobileFocusHeading,
    } = props;

    const { filteredFavoriteLists, searchQuery, setSearchQuery } = useSearch({
        items,
    });

    // if the CreateNewFolder view goes active, then the user goes back
    // the search results are still 'active' but the field will be empty
    // this can lead to an unrecoverable empty-state for the folder list
    // - thus this clears the search before opening the new view
    function interceptToggleCreateNewFolder() {
        setSearchQuery('');
        toggleCreateNewFolder();
    }

    const { currentPage, paginatedFolders, pagination, setCurrentPage } =
        usePagination({
            folders: filteredFavoriteLists,
            pageSize: 6,
            searchQuery,
            specialButtons: 0,
        });

    const mobileFirstFocusElement = useRef<HTMLDivElement>(null);
    const focusFirstItemRef = useRef<HTMLButtonElement>(null);
    const [shouldFocusFirstItem, setShouldFocusFirstItem] = useState(false);
    const setCurrentPageAndFocusFirstItem = useCallback(
        (page: number) => {
            setCurrentPage(page);
            setShouldFocusFirstItem(true);
        },
        [setCurrentPage],
    );

    useEffect(() => {
        if (mobileFocusHeading) {
            mobileFirstFocusElement.current?.focus();
        }
    }, [mobileFocusHeading]);

    useEffect(() => {
        if (
            shouldFocusFirstItem &&
            currentPage >= 0 &&
            focusFirstItemRef.current
        ) {
            focusFirstItemRef.current.focus();
            // Setting to false again to avoid the search input losing focus when typing
            setShouldFocusFirstItem(false);
        }
    }, [currentPage, shouldFocusFirstItem]);

    const footer: JSX.Element = <></>;
    const body: JSX.Element = (
        <>
            <div
                className="grid grid-cols-5 items-end gap-16 mb-16 outline-none"
                ref={mobileFirstFocusElement}
                tabIndex={mobileFocusHeading ? 0 : -1}
            >
                <div className="col-span-5 sm:col-span-3">
                    <Search
                        onSearch={(searchQuery) => setSearchQuery(searchQuery)}
                        initialFocusRef={initialFocusRef}
                    />
                </div>
                <Button
                    utility
                    onClick={interceptToggleCreateNewFolder}
                    className="col-span-5 sm:col-span-2 lt-sm:min-w-full"
                >
                    {i18n.t({
                        id: 'createNewList',
                        message: 'Create new list',
                    })}
                </Button>
            </div>
            {searchQuery !== '' && filteredFavoriteLists.length < 1 ? (
                <p className="pt-32 px-8 text-center text-m font-bold">
                    {i18n.t({
                        id: 'listNotFound',
                        message:
                            "Couldn't find any lists that match your search :(",
                    })}
                </p>
            ) : (
                <span />
            )}
            <Alert type="negative" show={!!errorMessage}>
                <p className="font-bold">
                    {i18n.t({
                        id: 'genericListErrorTitle',
                        message: 'Oops!',
                    })}
                </p>
                <p>
                    {errorMessage
                        ? errorMessage
                        : i18n.t({
                              id: 'genericListErrorMessage',
                              message:
                                  'There are gremlins about. Try again in a bit.',
                          })}
                </p>
            </Alert>
            <FoldersTemplate
                ref={focusFirstItemRef}
                itemType={itemType}
                itemId={itemId}
                api={api}
                closeCallback={closeCallback}
                folders={paginatedFolders}
                foldersWithItem={foldersWithItem}
                setErrorMessage={setErrorMessage}
                toggleCreateNewFolder={
                    currentPage !== 1 || searchQuery
                        ? undefined
                        : toggleCreateNewFolder
                }
            />
            <Pagination
                currentPage={currentPage}
                setCurrentPage={setCurrentPageAndFocusFirstItem}
                totalPages={pagination.totalPages}
            />
        </>
    );
    return [
        body,
        footer,
        <h1 className={modalClasses.titleText}>
            {i18n.t({
                id: 'chooseList',
                message: 'Add to a favourite list',
            })}
        </h1>,
    ];
};
